import {
  ShiftService,
  useAvailableServicesQuery,
  useCurrentShiftQuery,
} from "../../api";
import { useEffect, useRef, useState } from "react";

type ServiceOperation = "current_shift" | "available_services";

export type ServiceFailureContext = {
  operation: ServiceOperation;
  status?: number;
  kind: "permission" | "network" | "http" | "protocol";
};

const CURRENT_SHIFT_POLL_MS = 60_000;
const AVAILABLE_SERVICES_POLL_MS = 30_000;

export type ServiceAvailabilityStatus =
  | "checking"
  | "available"
  | "unavailable";

type ServiceStateOptions = {
  onPermissionDenied?: (context: { operation: ServiceOperation }) => void;
  onSessionInvalid?: (context: {
    operation: ServiceOperation;
    status?: number;
  }) => void;
  onFailure?: (context: ServiceFailureContext) => void;
  onRecovered?: () => void;
  /**
   * Whether the available-services signal participates at all. An exit kiosk
   * opts out: it serves customers who are already inside the store, so a screen
   * that blocks them has trapped them rather than turned them away. Opting out
   * also unsubscribes from the query, so its failures and stalls cannot reach
   * the exit flow through the generic unavailable state either.
   */
  trackMaintenance?: boolean;
};

const isSessionInvalid = (error: unknown) =>
  Boolean(
    error &&
      typeof error === "object" &&
      (error as { status?: unknown }).status === "SESSION_INVALID",
  );

const getRefreshStatus = (error: unknown) => {
  if (!error || typeof error !== "object") return undefined;
  const data = (error as { data?: unknown }).data;
  if (!data || typeof data !== "object") return undefined;
  const status = (data as { refresh_status?: unknown }).refresh_status;
  return typeof status === "number" ? status : undefined;
};

const getStatus = (error: unknown) => {
  if (!error || typeof error !== "object") return undefined;
  const candidate = error as { status?: number; originalStatus?: number };
  return candidate.status ?? candidate.originalStatus;
};

/**
 * Combines the current-shift and available-services signals that block the UI.
 * Current shift controls the out-of-service state when the store is manned or
 * the requested shift service is disabled. Available services controls
 * maintenance mode when full autonomous is turned off, and only for callers that
 * ask to track it. Either participating query failing fails closed with the
 * generic unavailable state and pauses both pollers when the kiosk session is no
 * longer usable.
 *
 * The two signals come from separate endpoints on separate polling clocks, so
 * they are never atomically consistent: around a shift change one can be a flip
 * ahead of the other, which is visible as a brief screen change and clears on
 * the next poll.
 */
export function useServiceState(
  targetService: ShiftService,
  {
    onPermissionDenied,
    onSessionInvalid,
    onFailure,
    onRecovered,
    trackMaintenance = true,
  }: ServiceStateOptions = {},
) {
  const [permissionDenied, setPermissionDenied] = useState(false);
  const [sessionInvalid, setSessionInvalid] = useState(false);
  const failureOpen = useRef(false);
  const queryOptions = {
    skip: permissionDenied || sessionInvalid,
    refetchOnFocus: !permissionDenied && !sessionInvalid,
    refetchOnReconnect: !permissionDenied && !sessionInvalid,
  };
  const {
    data: currentShift,
    error: currentShiftError,
  } = useCurrentShiftQuery(undefined, {
    ...queryOptions,
    pollingInterval: CURRENT_SHIFT_POLL_MS,
  });
  const {
    data: availableServices,
    error: availableServicesError,
  } = useAvailableServicesQuery(undefined, {
    ...queryOptions,
    skip: queryOptions.skip || !trackMaintenance,
    pollingInterval: AVAILABLE_SERVICES_POLL_MS,
  });
  const operation: ServiceOperation = currentShiftError
    ? "current_shift"
    : "available_services";
  const error =
    currentShiftError ?? (trackMaintenance ? availableServicesError : undefined);
  const shiftServices = currentShift?.result?.services;
  const services = availableServices?.result?.available_services;
  const hasValidCurrentShift =
    currentShift !== undefined &&
    currentShift.result !== undefined &&
    typeof currentShift.result.shift === "string" &&
    Array.isArray(shiftServices);
  const hasValidAvailableServices =
    availableServices !== undefined && Array.isArray(services);
  const hasData =
    hasValidCurrentShift && (!trackMaintenance || hasValidAvailableServices);

  useEffect(() => {
    if (isSessionInvalid(error)) {
      if (!sessionInvalid) {
        setSessionInvalid(true);
        onSessionInvalid?.({
          operation,
          status: getRefreshStatus(error),
        });
      }
      return;
    }
    if (error !== undefined && !failureOpen.current) {
      failureOpen.current = true;
      const status = getStatus(error);
      const rawStatus =
        error && typeof error === "object"
          ? (error as { status?: number | string }).status
          : undefined;
      onFailure?.({
        operation,
        status,
        kind:
          status === 403
            ? "permission"
            : typeof status === "number"
              ? "http"
              : rawStatus === "PARSING_ERROR"
                ? "protocol"
                : "network",
      });
    }
    if (error === undefined && hasData && failureOpen.current) {
      failureOpen.current = false;
      onRecovered?.();
    }
    if (getStatus(error) === 403 && !permissionDenied) {
      setPermissionDenied(true);
      onPermissionDenied?.({ operation });
    }
  }, [
    error,
    hasData,
    onFailure,
    onPermissionDenied,
    onRecovered,
    onSessionInvalid,
    operation,
    permissionDenied,
    sessionInvalid,
  ]);

  const isMaintenanceMode =
    trackMaintenance &&
    Array.isArray(services) &&
    !services.includes(targetService);
  const isShiftUnavailable =
    currentShift !== undefined &&
    (!hasValidCurrentShift ||
      currentShift.result.shift === "manned" ||
      (Array.isArray(shiftServices) &&
        !shiftServices.includes(targetService)));
  const isAvailableServicesUnavailable =
    trackMaintenance &&
    availableServices !== undefined &&
    !hasValidAvailableServices;
  const status: ServiceAvailabilityStatus =
    permissionDenied ||
    sessionInvalid ||
    error !== undefined ||
    isShiftUnavailable ||
    isAvailableServicesUnavailable
      ? "unavailable"
      : !hasData
        ? "checking"
        : "available";

  return {
    status,
    permissionDenied,
    isMaintenanceMode,
  };
}
